import { ChevronRight, Package, Receipt } from 'lucide-react';
import { Link } from 'react-router';
import type { LotMatch, OrderMatch } from '../api/types.js';
import {
  formatDateTime,
  formatPrice,
  lotHref,
  ORDER_STATUS_LABELS,
  ORIGIN_LABELS,
} from '../lib/format.js';
import { Badge } from './badge.js';

function unitsLabel(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? '' : 's'}`;
}

export function LotMatchList({ lots, query }: { lots: LotMatch[]; query: string }) {
  return (
    <section aria-labelledby="lot-results-title" className="flex flex-col gap-3">
      <h2 id="lot-results-title" className="text-lg font-semibold">
        {unitsLabel(lots.length, 'lote')} encontrado{lots.length === 1 ? '' : 's'}
      </h2>
      <ul className="flex flex-col gap-2">
        {lots.map((lot) => (
          <li key={lot.lotId}>
            <Link
              to={lotHref(lot.lotId, query)}
              className="flex items-center gap-4 rounded-card border border-border bg-card p-4 transition-colors duration-150 hover:border-primary focus-visible:outline-2 focus-visible:outline-ring"
            >
              <Package aria-hidden="true" className="size-6 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold break-all">{lot.lotId}</p>
                <p className="text-sm text-muted-foreground">
                  {lot.productName ?? lot.sku} · {ORIGIN_LABELS[lot.origin] ?? lot.origin} ·{' '}
                  {formatDateTime(lot.firstSeenAt)}
                </p>
              </div>
              <div className="hidden flex-wrap justify-end gap-2 sm:flex">
                <Badge>{unitsLabel(lot.unitsReceived, 'unidad')}</Badge>
                <Badge tone={lot.unitsAvailable > 0 ? 'success' : 'neutral'}>
                  {lot.unitsAvailable} en stock
                </Badge>
              </div>
              <ChevronRight aria-hidden="true" className="size-5 shrink-0 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function OrderLotLinks({ lots, query = '' }: { lots: OrderMatch['lots']; query?: string }) {
  if (lots.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {lots.map((lot) => (
        <li key={lot.lotId}>
          <Link
            to={lotHref(lot.lotId, query)}
            className="inline-flex min-h-11 items-center gap-1 rounded-control border border-border bg-card px-3 text-sm font-medium text-accent underline-offset-2 hover:underline"
          >
            {lot.quantity} × {lot.lotId}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function OrderMatchList({ orders, query }: { orders: OrderMatch[]; query: string }) {
  return (
    <section aria-labelledby="order-results-title" className="flex flex-col gap-3">
      <h2 id="order-results-title" className="text-lg font-semibold">
        {unitsLabel(orders.length, 'pedido')} con ese email
      </h2>
      <ul className="flex flex-col gap-3">
        {orders.map((order) => (
          <li
            key={order.publicId}
            className="flex flex-col gap-3 rounded-card border border-border bg-card p-4"
          >
            <div className="flex flex-wrap items-center gap-3">
              <Receipt aria-hidden="true" className="size-5 text-primary" />
              <p className="font-semibold">{formatDateTime(order.createdAt)}</p>
              <Badge tone={order.status === 'PAID' ? 'success' : 'neutral'}>
                {ORDER_STATUS_LABELS[order.status] ?? order.status}
              </Badge>
              <p className="ml-auto font-semibold">{formatPrice(order.total)}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              {order.lines.map((line) => `${String(line.quantity)} × ${line.sku}`).join(' · ')}
            </p>
            {order.lots.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">
                  Lotes que recibiste (abre uno para ver su origen):
                </p>
                <OrderLotLinks lots={order.lots} query={query} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Sin lotes: el pedido no llegó a pagarse, así que no se entregó ningún kit.
              </p>
            )}
            <p className="text-xs break-all text-muted-foreground">Pedido {order.publicId}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
